import { z } from 'zod'
import { calculateIntervalTimestamps, IntervalTimestampCalculationParams } from '@tapp.online/core'

// Input schema
const InputSchema = z.object({
    type: z.string(),
    versionInfo: z.object({
        firmwareVersion: z.string()
    }),
    uid: z.string(),
    startTime: z.number(),
    elapsedTime: z.number(),
    numberOfDatapointsAvailable: z.number(),
    numberOfMeasurementsOccurred: z.number(),
    interval: z.number(),
    datapoints: z.object({
        temperature: z.array(z.number()).optional(),
        humidity: z.array(z.number()).optional()
    }),
    rangeStart: z.object({
        temperature: z.number()
    }),
    rootMeanSquaredError: z.object({
        temperature: z.number()
    }),
    currentValues: z.object({
        temperature: z.number()
    }),
    validConfig: z
        .object({
            minTemperature: z.number().optional(),
            maxTemperature: z.number().optional(),
            minHumidity: z.number().optional(),
            maxHumidity: z.number().optional()
        })
        .optional(),
    statuses: z.array(z.string()),
    checksum: z.string().optional(),
    batteryStatus: z.number().optional(),
    references: z
        .object({
            label: z.string().optional().nullable()
        })
        .optional(),
    identifiers: z
        .object({
            label: z.string().optional().nullable()
        })
        .optional()
})

// Output schema focusing on matching structures
const OutputSchema = z.object({
    type: z.string(),
    label: z.object({
        uid: z.string(),
        sequenceCode: z.string().optional().nullable()
    }),
    senseConfig: z.object({
        interval: z.number(),
        limits: z
            .object({
                minTemperature: z.number().optional(),
                maxTemperature: z.number().optional(),
                minHumidity: z.number().optional(),
                maxHumidity: z.number().optional()
            })
            .optional()
    }),
    calculatedSenseData: z.object({
        datapoints: z.array(
            z.object({
                timestamp: z.number(),
                temperature: z.number().optional(),
                humidity: z.number().optional()
            })
        ),
        numberOfDatapointsRead: z.number(),
        numberOfDatapointsAvailable: z.number(),
        numberOfMeasurementsOccurred: z.number(),
        statuses: z.array(z.string()),
        startTime: z.number(),
        elapsedTime: z.number(),
        currentTime: z.number(),
        preciseElapsedTime: z.number(),
        preciseInterval: z.number(),
        versionInfo: z.object({
            firmwareVersion: z.string()
        }),
        batteryStatus: z.number().optional(),
        measurementSnapshot: z
            .object({
                timestamp: z.number().optional(),
                temperature: z.number().optional(),
                humidity: z.number().optional()
            })
            .optional()
    }),
    alert: z.object({
        level: z.number()
    }),
    labelReference: z.string().optional().nullable()
})

const determineAlertLevel = (statuses: string[]) => {
    // TODO: this determination is really rough
    if (statuses.includes('TEMPERATURE_TOO_HIGH') || statuses.includes('TEMPERATURE_TOO_LOW') || statuses.includes('HUMIDITY_TOO_HIGH') || statuses.includes('HUMIDITY_TOO_LOW'))
        return 1
    if (statuses.includes('LOGGING')) return 0
    else return -1
}

const transformLabelTapData = (labelTap: any) => {
    const data = { ...labelTap.senseData, references: { ...labelTap.references }, identifiers: { ...labelTap.identifiers } }
    const endTime = Math.floor(new Date(labelTap.created).getTime() / 1000)

    try {
        return transformData({ data, endTime })
    } catch (error) {
        console.log('error', error)
        return null
    }
}

const transformData = ({
    data,
    endTime,
    intervalTimestampData
}: {
    data: z.infer<typeof InputSchema>
    endTime?: number
    intervalTimestampData?: { intervalTimestamps: number[]; preciseInterval: number }
}) => {
    const payload: IntervalTimestampCalculationParams = {
        startTime: data.startTime,
        interval: data.interval,
        elapsedTime: data.elapsedTime,
        numberOfMeasurementsOccurred: data.numberOfMeasurementsOccurred,
        endTime: endTime
    }

    const calculatedIntervalTimestamps = calculateIntervalTimestamps(payload)
    const intervalTimestamps = intervalTimestampData?.intervalTimestamps ? intervalTimestampData.intervalTimestamps : calculatedIntervalTimestamps.intervalTimestamps
    const preciseInterval = intervalTimestampData?.preciseInterval ? intervalTimestampData.preciseInterval : calculatedIntervalTimestamps.preciseInterval

    const parsed = InputSchema.parse(data)
    const alertLevel = determineAlertLevel(parsed.statuses)

    const timingFactor = isNaN(parsed.interval / preciseInterval) ? 1 : parsed.interval / preciseInterval
    const preciseElapsedTime = timingFactor * parsed.elapsedTime

    const currentTime = parsed.startTime + preciseElapsedTime

    return OutputSchema.parse({
        type: parsed.type,
        label: {
            uid: parsed.uid,
            sequenceCode: parsed.identifiers?.label
        },
        senseConfig: {
            interval: parsed.interval,
            limits: parsed.validConfig
        },
        calculatedSenseData: {
            datapoints: intervalTimestamps.map((timestamp, index) => {
                const temperature = parsed.datapoints?.temperature?.[index]
                const humidity = parsed.datapoints?.humidity?.[index]

                return {
                    ...(temperature && { temperature }),
                    ...(humidity && { humidity }),
                    timestamp: timestamp
                }
            }),
            numberOfDatapointsAvailable: parsed.numberOfDatapointsAvailable,
            numberOfDatapointsRead: parsed.numberOfDatapointsAvailable,
            numberOfMeasurementsOccurred: parsed.numberOfMeasurementsOccurred,
            statuses: parsed.statuses,
            startTime: parsed.startTime,
            elapsedTime: parsed.elapsedTime,
            currentTime: currentTime,
            preciseElapsedTime: preciseElapsedTime,
            preciseInterval: preciseInterval,
            versionInfo: {
                firmwareVersion: parsed.versionInfo.firmwareVersion
            },
            batteryStatus: parsed.batteryStatus,
            measurementSnapshot: {
                temperature: parsed.currentValues.temperature
            }
        },
        alert: {
            level: alertLevel
        },
        labelReference: parsed.references?.label
    })
}

export { transformData, transformLabelTapData }
